import React, { useState } from "react";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, X } from "lucide-react";
import { UserForm } from "./UserForm";
import { UserStats } from "./UserStats";

/**
 * UserModal — admin can view/edit/delete a user, or create a new one.
 * Pass `user=null` for create mode, or a user object for edit mode.
 */
export function UserModal({ user, onClose, onSaved }) {
    const isCreate = !user;
    const [form, setForm] = useState({
        email: user?.email || "",
        name: user?.name || "",
        role: user?.role || "sponsor",
        phone: user?.phone || "",
        siren: user?.siren || "",
        status: user?.status || "approved",
        password: "",
    });
    const [saving, setSaving] = useState(false);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const validate = () => {
        if (!form.email.trim()) return "Email requis";
        if (isCreate && form.password.length < 6) return "Mot de passe (6 caractères min)";
        const phone = form.phone.replace(/[\s.\-]/g, "");
        if (!/^0\d{9}$/.test(phone)) return "Téléphone invalide (10 chiffres FR)";
        if (form.role === "sponsor") {
            const s = (form.siren || "").replace(/\s/g, "");
            if (!/^\d{9}$/.test(s)) return "SIREN invalide (9 chiffres)";
        }
        return null;
    };

    const buildPayload = () => ({
        email: form.email.trim(),
        name: form.name.trim(),
        phone: form.phone,
        siren: form.role === "sponsor" ? form.siren : (isCreate ? null : ""),
        status: form.status,
        role: form.role,
    });

    const save = async () => {
        const err = validate();
        if (err) { toast.error(err); return; }
        setSaving(true);
        try {
            if (isCreate) {
                await api.post("/admin/users", { ...buildPayload(), password: form.password });
                toast.success("Utilisateur créé");
            } else {
                const payload = buildPayload();
                if (form.password) payload.password = form.password;
                await api.put(`/admin/users/${user.id}`, payload);
                toast.success("Utilisateur mis à jour");
            }
            onSaved?.();
            onClose();
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        } finally { setSaving(false); }
    };

    const remove = async () => {
        if (!user) return;
        if (!window.confirm(`Supprimer définitivement ${user.email} ?`)) return;
        try {
            await api.delete(`/admin/users/${user.id}`);
            toast.success("Utilisateur supprimé");
            onSaved?.();
            onClose();
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
            onClick={onClose}
            data-testid="user-modal"
        >
            <div
                className="bg-zinc-950 border border-zinc-800 max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.25em] text-orange-500 font-bold">
                            {isCreate ? "Nouvel utilisateur" : "Détails utilisateur"}
                        </div>
                        <h3 className="font-display text-3xl uppercase tracking-tight">
                            {isCreate ? "Créer un compte" : (user.name || user.email)}
                        </h3>
                        {!isCreate && (
                            <div className="text-xs text-zinc-500 mt-1">
                                ID : <span className="font-mono">{user.id}</span> · Inscrit le {(user.created_at || "").slice(0, 10)}
                            </div>
                        )}
                    </div>
                    <button onClick={onClose} className="text-zinc-500 hover:text-white">
                        <X size={20} />
                    </button>
                </div>

                <UserForm form={form} set={set} isCreate={isCreate} />

                {!isCreate && <UserStats user={user} />}

                <div className="flex flex-wrap gap-2 pt-2">
                    <button data-testid="user-modal-save" onClick={save} disabled={saving} className="btn-primary py-2 px-4 text-xs">
                        {saving ? "…" : isCreate ? "Créer" : "Enregistrer"}
                    </button>
                    {!isCreate && (
                        <button
                            data-testid="user-modal-delete"
                            onClick={remove}
                            className="px-4 py-2 border border-red-900 hover:bg-red-950 text-red-400 text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1 ml-auto"
                        >
                            <Trash2 size={14} /> Supprimer
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
